import type { NightmareType } from './nightmares.ts';
import { sec } from './units.ts';

/**
 * Elite incursions (DECISIONS 2026-10-23): a crypt under the titan's shell for 1 to 8 heroes playing
 * live together, and absent friends' spectres (2026-11-03). Four rooms that lock behind the party until they are cleared, then the Marrow Warden.
 * Fallen heroes don't come back on their own: an ally has to stand over them. Always hard.
 */
/**
 * The party level (the average) from which the difficulty eases for the unique pieces that now come later
 * (docs/UNICAS.md §5): the armour came at 10 before. Below it everything is as it was; it eases over `rampLevels`
 * levels, so no single level drops the crypt by half.
 */
export const UNIQUE_EASE = { fromLevel: 10, rampLevels: 4 };

/**
 * How far the toughness has eased for the unique pieces that come later now (docs/UNICAS.md §5), in bp, from the
 * party's levels above 1 (×100): 0 below `UNIQUE_EASE.fromLevel`, then a step per level up to all of it.
 */
export const easeShare = (above: number): number => {
  const from = (UNIQUE_EASE.fromLevel - 1) * 100;
  if (above < from) return 0;
  return Math.min(10000, Math.trunc(((above - from + 100) * 10000) / (UNIQUE_EASE.rampLevels * 100)));
};

export const DUNGEON = {
  /**
   * One player may go down alone, or with absent friends' spectres (2026-11-03, asked by the players): the crypt is
   * filled for the spectres as for live heroes. Was 2.
   */
  minLive: 1,
  maxHeroes: 8,
  /** Each spectre's player takes this share of the XP a live hero took for the run (as an incursion's ghosts). */
  spectreShareBp: 1000,
  /** Rooms before the boss (the first one is the safe entrance). */
  rooms: 3,
  roomRadius: 3,
  entryRadius: 2,
  bossRadius: 5,
  corridor: 3,
  /** Nightmare points per room wave, per hero, before level scaling; two waves per room. */
  roomPoints: [16, 21, 27],
  wavesPerRoom: 2,
  /** A breath between a room's waves. */
  waveGap: sec(4),
  /** A room wakes with friends still in the corridor: they walk in, the door held open this long at most. */
  joinTicks: sec(4),
  /** Elites in the last room, per hero beyond the first (rounded down) plus one. */
  lastRoomElites: 1,
  /** Every room is one grade darker: share of inked nightmares. */
  inkBp: [1500, 3000, 4500],
  /** Party-size scaling of the nightmares by heroes (1 to 8; past 4, BIG_PARTY): bigger parties carry more. */
  partyBp: [0, 10000, 17000, 27000, 38000],
  /**
   * Level scaling: +15 % per average hero level above 1 (all crypts but the deep one). Was 9 %: real groups
   * won 85 % of crypts and 76 % in embers; with 15 % the same fights give about 65 % and 43 % (balance:mirror).
   * 19 % since 2026-10-29: the embers crypt was back at 57 % for people, and the new gear slots (docs/EQUIPO.md)
   * filled lift both crypts ~10–25 points more; with 19 % it is 58 % and 46 % today, 67 % and 68 % with the slots full.
   * 23 % since the class balance (docs/BALANCE_CLASES.md): skills grow with the level, and 23 % keeps both crypts at least
   * as hard for people (balance:mirror, bots 67 % and 40 % against 68 % and 40 % before).
   */
  perLevelBp: 2300,
  /**
   * Past this average level, each level brings fewer more nightmares (`countPerLevelBp` instead of `perLevelBp`), with more
   * health each and harder blows, the Warden's too (bp per level). Below it, `perLevelBp` alone, for numbers and damage.
   * Since 2026-10-31: before, the level only brought more nightmares of level-1 health, and groups of level 12 and up
   * cleared a room in 9 s losing a tenth of their health (each nightmare lived under a second). For people, by balance:mirror:
   * crypt 65 → 61 %, embers 55 → 42 % (groups since 2026-10-28: 87 → 78 % and 99 → 78 %).
   */
  fromLevel: 8,
  countPerLevelBp: 1000,
  hpPerLevelBp: 6000,
  dmgPerLevelBp: 9000,
  /**
   * On top of the level: every nightmare's health and blows (`toughHpBp`, `toughDmgBp`, all parties), and more for each
   * artificer in the party (`artificerHpBp`, `artificerDmgBp`, added per artificer): since 2026-11-02 its automaton draws the
   * crypt's nightmares (docs/DECISIONS.md), which made the crypts easier for parties with one. For people, by
   * balance:mirror against main: crypt 60 → 57 %, embers 42 → 40 %; parties with an artificer 68 → 66 % and 49 → 48 %.
   */
  toughHpBp: 10500,
  toughDmgBp: 10500,
  /**
   * From `UNIQUE_EASE.fromLevel` (where the unique armour used to come), the toughness above eased: the armour and the
   * amulet moved to levels 20 and 40 (docs/UNICAS.md §5), and balance:mirror without them gave this to win what people won.
   * Below that level nothing changed: the heroes didn't have them before either.
   */
  easedHpBp: 9000,
  easedDmgBp: 9000,
  artificerHpBp: 1000,
  artificerDmgBp: 1000,
  /** Fallen heroes: an ally standing this close for this long brings them back at this share of HP. */
  reviveRange: 900,
  reviveTicks: sec(2.5),
  reviveHpBp: 4000,
  /** Clearing a room: the fallen get up and everyone catches their breath. */
  clearReviveHpBp: 3000,
  clearHealBp: 5000,
  /** Stalkers only show in torchlight: this close to a standing hero. */
  torchRange: 2600,
  /** What lives down there, room by room (weights). */
  bestiary: [
    { goblin: 3, skeleton: 3, wolf: 2, slinger: 2, crow: 1 },
    { skeleton: 3, wraith: 2, stalker: 2, goblin: 1, slinger: 1, mourner: 1 },
    { skeleton: 2, wraith: 2, slime: 2, gargoyle: 1, stalker: 1, mourner: 1, ogre: 1 },
  ] as Partial<Record<NightmareType, number>>[],
};

/** The Marrow Warden: a colossus of bone and shell that reads the floor like a drum. */
export const WARDEN = {
  /** Health by party size (1 to 8 heroes; past 4, BIG_PARTY), before level scaling. */
  hpByParty: [0, 2000, 2850, 5800, 11800],
  /** Its cleave is lighter on a small party (bp of its damage). */
  meleeByParty: [0, 6500, 7500, 9000, 10000, 10000, 10000, 10000, 10000],
  cleaveRadius: 800,
  /** Slam: a circle under a hero that lands after the warning. */
  slamRadius: 1500,
  slamDamage: 120,
  slamWarn: sec(1.5),
  /** Nova (get away) and ring (come close): the safe line between them. */
  novaRadius: 2300,
  novaDamage: 95,
  novaWarn: sec(2),
  /** Seconds between abilities per phase; phase 2 below 60 %, phase 3 below 25 %. */
  every: [sec(5), sec(4), sec(3)],
  phase2AtBp: 6000,
  phase3AtBp: 2500,
  phase3SpeedBp: 13000,
  /** At each phase change it roars: invulnerable for a moment and calls bones from the walls. */
  roarTicks: sec(2),
  roarAdds: [0, 1, 2, 3, 4, 5, 5, 6, 6],
  summonEvery: 3,
  summonCount: [0, 1, 1, 2, 3, 3, 4, 4, 4],
  /** Phase 3 slams leave ink that burns whoever stands in it. */
  poolRadius: 1200,
  poolTicks: sec(7),
  poolDamage: 7,
  poolEvery: 6,
};

/** What a won crypt gives each hero (the first win of the day; later runs give XP only). */
export const DUNGEON_REWARD = {
  dust: 120,
  xp: 600,
  /** Shared, into the storage. */
  res: 80,
  relicBp: 4000,
  /** XP per room cleared on a loss, or on a replay the same day. */
  xpPerRoom: 100,
};

/**
 * The crypt in embers (level II, docs/NIVELES.md): from group level 5, once the group has won the
 * crypt. Longer — five rooms before the Warden — with lava cracks in the rooms, fire vents that erupt on
 * a rhythm (mostly in the corridors: time the crossing), and a Warden whose slams raise eruptions. Its own
 * chest, once a day per hero, apart from the plain crypt's.
 */
export const CRYPT_ASCUAS = {
  minGroup: 5,
  rooms: 5,
  roomPoints: [19, 25, 31, 36, 41],
  inkBp: [1500, 3000, 4500, 5500, 6500],
  bestiary: [
    { goblin: 3, skeleton: 3, wolf: 2, slinger: 2, crow: 1 },
    { skeleton: 3, wraith: 2, stalker: 2, goblin: 1, slinger: 1, mourner: 1 },
    { skeleton: 2, wraith: 2, slime: 2, gargoyle: 1, stalker: 1, crow: 2 },
    { skeleton: 3, gargoyle: 2, wraith: 2, slime: 1, mourner: 1, ogre: 1 },
    { skeleton: 2, gargoyle: 2, wraith: 2, stalker: 2, ogre: 1 },
  ] as Partial<Record<NightmareType, number>>[],
  /** Everything down here is tougher than in the plain crypt: nightmares' health and everyone's damage (bp). */
  enemyHpBp: 15000,
  /** 130 % until the class balance (docs/BALANCE_CLASES.md): with the warrior's cleave the embers crypt got easier; 145 % keeps it at least as hard (balance:mirror, bots 38 % against 40 %). */
  dmgBp: 14500,
  /** From `UNIQUE_EASE.fromLevel`, as in DUNGEON (balance:mirror without the early armour and amulet: 40 → 40 %). */
  easedHpBp: 12250,
  easedDmgBp: 12250,
  /** Party-size scaling of the nightmares by heroes (1 to 8; past 4, BIG_PARTY): bigger parties carry more here than in the plain crypt. */
  partyBp: [0, 10000, 17000, 28000, 43000],
  /** Elites in the last room before the seal, per hero beyond the first (rounded down) plus this. */
  lastRoomElites: 2,
  /** Clearing a room heals less than in the plain crypt: five rooms wear the party down. */
  clearHealBp: 3000,
  /** The Warden, tougher than in the plain crypt (bp of its health, by party size 1 to 8; ×1,2 to ×1,55), and its slams raise eruptions. */
  bossHpBp: [0, 15000, 13500, 12000, 15500, 15500, 15500, 15500, 15500],
  wardenSlamEvery: sec(9),
  wardenSlamEruptions: 1,
  /** Lava cracks per room (not the entrance nor the Warden's hall). */
  cracksPerRoom: 2,
  /** Fire vents: one in every corridor and one in every room; each erupts every `ventEvery`, warned `ventWarn` before. */
  ventEvery: sec(4),
  ventWarn: sec(1.5),
  ventRadius: 700,
  /** The chest: a rare piece, embers, dust, XP and a better shot at a relic. */
  embers: 6,
  dust: 160,
  xp: 900,
  relicBp: 6000,
  /**
   * The Warden's door is sealed: three runes in the last room light up in an order once it is cleared; step on
   * them in that order. A hero counts on a plate up to its rim and a bit past it (the stone is ~485 to a corner).
   */
  seal: { plates: 3, showTicks: 12, plateRange: 620, replayDelay: 24 },
};

/**
 * The deep crypt (level III, docs/NIVELES.md): three floors in one descent — stone, embers, obsidian. Each
 * floor: its entrance, a fighting room, a puzzle room and its guardian's hall. After each guardian the party
 * goes down the stairs or walks out with what it has; falling keeps half. One chest a day per hero.
 */
export const DEEP_CRYPT = {
  minGroup: 8,
  floors: 3,
  /** Nightmare points per wave, per floor: [fighting room, puzzle room]. */
  roomPoints: [
    [20, 12],
    [27, 15],
    [28, 16],
  ],
  inkBp: [2500, 4500, 6500],
  bestiary: [
    { goblin: 3, skeleton: 3, wolf: 2, stalker: 1, slinger: 2 },
    { skeleton: 3, wraith: 2, slime: 2, gargoyle: 1, mourner: 1, ogre: 1 },
    { skeleton: 2, gargoyle: 2, wraith: 2, stalker: 2, mourner: 1, ogre: 1 },
  ] as Partial<Record<NightmareType, number>>[],
  /** Over the glass bridge only fliers come. */
  flyers: { gargoyle: 2, wraith: 3, crow: 3 } as Partial<Record<NightmareType, number>>,
  enemyHpBp: [14000, 15500, 16000],
  dmgBp: [12000, 13000, 13500],
  /** By heroes, 1 to 8 (past 4, BIG_PARTY). */
  partyBp: [0, 10000, 15000, 28000, 52000],
  /**
   * Level scaling here: +30 % per average level. It was 9 % (real groups came down at level 10 and won 92 %), then 22 %
   * (2026-10-28); 30 % since 2026-10-29 for the new gear slots (docs/EQUIPO.md): about 32 % for people today, 47 % with them full.
   * 36 % since the class balance (skills grow with the level): the same fights as before for people (balance:mirror, bots 38 %).
   */
  perLevelBp: 3600,
  /** Past this average level, fewer more nightmares, tougher and fiercer, the guardians' blows too (as in DUNGEON; 2026-10-31: 43 → 29 % for people, groups since 2026-10-28 74 → 50 %). */
  fromLevel: 8,
  countPerLevelBp: 2000,
  hpPerLevelBp: 4000,
  dmgPerLevelBp: 6000,
  /** On top of the level, as in DUNGEON: all parties, and more per artificer in the party (2026-11-02: 30 → 27 % for people; with an artificer 16 → 15 %). */
  toughHpBp: 10500,
  toughDmgBp: 10500,
  /** From `UNIQUE_EASE.fromLevel`, as in DUNGEON (balance:mirror without the early armour and amulet: 27 → 26 %). */
  easedHpBp: 6000,
  easedDmgBp: 6000,
  artificerHpBp: 1500,
  artificerDmgBp: 1500,
  clearHealBp: 3500,
  /** Guardians' health by party size (1 to 8; past 4, BIG_PARTY), before level scaling: the Warden, the magma lord, the colossus. */
  guardianHp: [
    [0, 1400, 1800, 4000, 8500],
    [0, 2600, 3300, 7400, 17500],
    [0, 3200, 4000, 9000, 23000],
  ],
  /**
   * Each guardian's health and damage on top (bp): the Warden, the magma lord, the colossus. Since 2026-10-30 each one is
   * harder than the one before and the colossus clearly the hardest: of the groups that reach it (real heroes, bots), the
   * Warden falls 86 % of the time, the magma lord 80 % (it was 100 %, no one ever lost to it) and the colossus 57 % (was 77 %).
   * Since 2026-10-31 less health and more damage: the magma lord and the colossus took two minutes and the group never fell
   * below 70 %. On the real groups' runs (bots): Warden 99 % in 22 s, magma lord 81 % in 59 s, colossus 46 % in 82 s.
   */
  guardianHpBp: [8000, 16000, 11000],
  guardianDmgBp: [11000, 20000, 19000],
  /** Ticks every standing hero must stand together on the stairs or the way out. */
  chooseTicks: 24,
  stairsRange: 900,
  /** Puzzles: a reinforcement every `pressEvery` while unsolved (and one on every mistake), of this many points per hero. */
  pressEvery: sec(20),
  pressPoints: 6,
  braziers: { count: 4, lightTicks: 12, burnTicks: sec(10), range: 800 },
  levers: { rounds: 2, holdTicks: sec(3), range: 650 },
  runes: { plates: 4, showTicks: 12, plateRange: 620, replayDelay: 24 },
  glass: { showTicks: sec(4), fallDamage: 45, range: 520 },
  /** Per floor cleared, into the chest: XP, dust, shards (and embers from the second floor). */
  chest: [
    { xp: 400, dust: 60, shards: 1, embers: 0 },
    { xp: 600, dust: 90, shards: 2, embers: 3 },
    { xp: 1000, dust: 140, shards: 4, embers: 3 },
  ],
  /** The day's piece and relic: from the second floor, a rare piece; all three floors, a better relic shot. */
  pieceFrom: 2,
  relicBp: [0, 3000, 7000],
  /** Falling keeps this share of the chest. */
  fallBp: 5000,
  xpPerRoom: 100,
};
